import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const BIENES_OPTIONS = [
  { id: 'propiedades', label: 'Propiedades o inmuebles' },
  { id: 'vehiculos', label: 'Vehículos' },
  { id: 'cuentas', label: 'Cuentas bancarias o dinero' },
  { id: 'otros', label: 'Otros bienes' },
  { id: 'no-seguro', label: 'No estoy seguro' },
];

const fireFormConversion = () => {
  if (typeof (window as Window & { gtag?: (...args: unknown[]) => void }).gtag === 'function') {
    (window as Window & { gtag: (...args: unknown[]) => void }).gtag('event', 'conversion', {
      send_to: 'AW-17106059641/i4UoCPuwkYscEPmC59w_',
      value: 1.0,
      currency: 'CLP',
    });
  }
};

const PosesionForm = () => {
  const [bienes, setBienes] = useState<string[]>([]);
  const [comentarios, setComentarios] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const toggleBien = (id: string) => {
    setBienes(prev =>
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();

    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    // Bienes seleccionados como texto
    body.append('bienes_herencia', bienes.map(b => BIENES_OPTIONS.find(o => o.id === b)?.label ?? b).join(', '));

    try {
      const response = await fetch('https://readdy.ai/api/form/d6qtmt3a90d3mf3egn2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        fireFormConversion();
        form.reset();
        setBienes([]);
        setComentarios('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <span className="inline-block bg-primary-yellow text-black text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Posesión Efectiva · Chile
          </span>
          <h1 className="text-3xl lg:text-4xl font-bold text-black mb-4 leading-tight">
            Consulta inicial –<br />Posesión efectiva
          </h1>
          <p className="text-gray-600 text-base leading-relaxed">
            Complete este formulario con algunos antecedentes generales. Nuestro equipo revisará la información y lo contactará para coordinar una reunión donde explicaremos el proceso de tramitación de la posesión efectiva.
          </p>
        </motion.div>

        {/* Steps summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-10"
        >
          {[
            { num: '01', text: 'Contacto inicial y revisión de antecedentes' },
            { num: '02', text: 'Preparación y presentación de la solicitud' },
            { num: '03', text: 'Seguimiento hasta la resolución del trámite' },
          ].map(step => (
            <div key={step.num} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <span className="text-primary-yellow font-black text-lg block mb-1">{step.num}</span>
              <p className="text-xs text-gray-600 leading-snug">{step.text}</p>
            </div>
          ))}
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {submitStatus === 'success' ? (
            <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-xl font-bold text-black mb-3">¡Solicitud recibida!</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Hemos recibido su solicitud. Nuestro equipo revisará los antecedentes y lo contactará para coordinar una reunión inicial y analizar el proceso de tramitación de la posesión efectiva.
              </p>
              <Link
                to="/"
                className="inline-block bg-primary-yellow text-black px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                Volver al inicio
              </Link>
            </div>
          ) : (
            <form
              id="posesion-efectiva-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-gray-50 rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm space-y-8"
            >
              {/* Datos de contacto */}
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-black mb-5 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center bg-primary-yellow rounded-full text-xs font-black">1</span>
                  Datos de contacto
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-black mb-1.5">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      required
                      placeholder="Ej: Juan Pérez González"
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-black mb-1.5">
                      Correo electrónico <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="correo@ejemplo.com"
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-semibold text-black mb-1.5">
                      Teléfono o WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      required
                      placeholder="+56 9 1234 5678"
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Antecedentes generales */}
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-black mb-5 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center bg-primary-yellow rounded-full text-xs font-black">2</span>
                  Antecedentes generales
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nombre_fallecido" className="block text-sm font-semibold text-black mb-1.5">
                      Nombre del fallecido <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nombre_fallecido"
                      name="nombre_fallecido"
                      required
                      placeholder="Nombre completo del causante"
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="anio_fallecimiento" className="block text-sm font-semibold text-black mb-1.5">
                        Año de fallecimiento <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="anio_fallecimiento"
                        name="anio_fallecimiento"
                        required
                        min={1900}
                        max={new Date().getFullYear()}
                        placeholder="Ej: 2023"
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="num_herederos" className="block text-sm font-semibold text-black mb-1.5">
                        N.º aproximado de herederos <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        id="num_herederos"
                        name="num_herederos"
                        required
                        min={1}
                        placeholder="Ej: 3"
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bienes de la herencia */}
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-black mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center bg-primary-yellow rounded-full text-xs font-black">3</span>
                  Información básica de la herencia
                </h3>
                <p className="text-xs text-gray-500 mb-4 ml-8">¿Qué bienes tenía el fallecido? (selección múltiple)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-0">
                  {BIENES_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleBien(option.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 cursor-pointer text-left whitespace-nowrap ${
                        bienes.includes(option.id)
                          ? 'border-primary-yellow bg-primary-yellow/10 text-black'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className={`w-5 h-5 flex items-center justify-center rounded flex-shrink-0 border-2 transition-all ${
                        bienes.includes(option.id)
                          ? 'bg-primary-yellow border-primary-yellow'
                          : 'border-gray-300 bg-white'
                      }`}>
                        {bienes.includes(option.id) && (
                          <i className="ri-check-line text-xs text-black font-black"></i>
                        )}
                      </span>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comentarios opcionales */}
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-black mb-5 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded-full text-xs font-black text-gray-500">4</span>
                  Información adicional
                  <span className="text-xs font-normal text-gray-400 normal-case tracking-normal">(opcional)</span>
                </h3>
                <textarea
                  id="comentarios"
                  name="comentarios"
                  value={comentarios}
                  onChange={e => {
                    if (e.target.value.length <= 500) setComentarios(e.target.value);
                  }}
                  rows={4}
                  placeholder="Comentarios o antecedentes adicionales del caso..."
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-primary-yellow focus:outline-none transition-colors text-black text-sm bg-white resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{comentarios.length}/500 caracteres</p>
              </div>

              {/* Sección final */}
              <div className="bg-black rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-yellow rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-calendar-check-line text-black text-lg"></i>
                </div>
                <div>
                  <p className="text-white text-sm leading-relaxed">
                    Un asesor revisará su información y lo contactará para coordinar una <strong className="text-primary-yellow">reunión inicial</strong> y explicarle el proceso de posesión efectiva.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-yellow text-black py-4 rounded-full font-bold text-base hover:bg-yellow-400 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill text-lg"></i>
                    Solicitar revisión del caso
                  </span>
                )}
              </button>

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                  <i className="ri-error-warning-line text-lg flex-shrink-0"></i>
                  <span>Hubo un error al enviar. Por favor intente nuevamente.</span>
                </div>
              )}
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PosesionForm;
